export const FIRST_SOURCE_STORAGE_KEY = 'wg_first_source_v1';

export interface FirstSource {
  params: Record<string, string>;
  referrerOrigin: string | null;
}

type SourceLocation = Pick<Location, 'origin' | 'search'>;
type SourceStorage = Pick<Storage, 'getItem' | 'setItem'>;

function browserStorage(): SourceStorage | null {
  try { return typeof window === 'undefined' ? null : window.localStorage; }
  catch { return null; }
}

function marketingParams(entries: Iterable<[string, unknown]>): Record<string, string> {
  const params: Record<string, string> = {};
  for (const [rawKey, rawValue] of entries) {
    const key = rawKey.toLowerCase();
    if (!/^(utm_[a-z0-9_]{1,48}|gclid|fbclid|campaign|campaign_id|media_source|pid|c)$/.test(key)) continue;
    if (/(^|_)(token|uid|email|password|secret|recovery)(_|$)/.test(key)) continue;
    if (typeof rawValue !== 'string' || /@|%40|[\u0000-\u001f\u007f]/i.test(rawValue)) continue;
    const value = rawValue.trim().slice(0, 256);
    if (!value || Object.hasOwn(params, key)) continue;
    params[key] = value;
    if (Object.keys(params).length >= 32) break;
  }
  return params;
}

function httpOrigin(value: unknown): string | null {
  if (typeof value !== 'string' || !value) return null;
  try {
    const url = new URL(value);
    return /^https?:$/.test(url.protocol) ? url.origin : null;
  } catch { return null; }
}

/** Read only the supported public marketing fields, even from older stored data. */
export function readFirstSource(storage: SourceStorage | null = browserStorage()): FirstSource | null {
  try {
    const value: unknown = JSON.parse(storage?.getItem(FIRST_SOURCE_STORAGE_KEY) ?? 'null');
    if (!value || typeof value !== 'object' || !('params' in value) || !value.params || typeof value.params !== 'object' || Array.isArray(value.params)) return null;
    const params = marketingParams(Object.entries(value.params));
    const referrerOrigin = httpOrigin('referrerOrigin' in value ? value.referrerOrigin : null);
    return Object.keys(params).length || referrerOrigin ? { params, referrerOrigin } : null;
  } catch { return null; }
}

/** First campaign wins as a whole; internal navigation never changes its fields. */
export function captureFirstSource(location: SourceLocation, referrer: string, storage: SourceStorage | null = browserStorage()): FirstSource | null {
  const existing = readFirstSource(storage);
  if (existing) return existing;
  const params = marketingParams(new URLSearchParams(location.search));
  const candidateOrigin = httpOrigin(referrer);
  const referrerOrigin = candidateOrigin === httpOrigin(location.origin) ? null : candidateOrigin;
  if (!Object.keys(params).length && !referrerOrigin) return null;
  const source = { params, referrerOrigin };
  try { storage?.setItem(FIRST_SOURCE_STORAGE_KEY, JSON.stringify(source)); }
  catch { /* Storage denial must not prevent entry or game navigation. */ }
  return source;
}
